import { useContractOrdererStore } from '@/hooks/use-contract-orderer'
import { useI18n } from 'i18n-utils'
import { useBaseInfoStore, useDoubleContractKlineStore, useExchangeInfoStore } from 'store'
import { CustomButton, HocElSlider, InputView, MinusIcon, PlusIcon2, SizedBox, type Arrayable, type InputViewExpose } from 'ui-components'
import { DefineComponentProps } from 'utils'
import '../styles/update-leverage-layout.scss'

type UpdateLeverageProps = {
  onSuccess?: () => void
}

export const UpdateLeverage = defineComponent<UpdateLeverageProps>({
  props: DefineComponentProps<UpdateLeverageProps>(['onSuccess']),
  setup(props) {
    const orderer = useContractOrdererStore()
    const inputLeverage = ref(orderer.currentLeverage)
    const inputLeverageRef = ref<InputViewExpose>()
    const klineStore = useDoubleContractKlineStore()
    const exchangeInfo = useExchangeInfoStore()

    const baseInfoStore = useBaseInfoStore()
    const { t } = useI18n()

    const handleMinus = () => {
      if (inputLeverage.value <= 1) {
        inputLeverage.value = 1
        return
      }
      inputLeverage.value--
    }
    const handlePlus = () => {
      const currentSymbolInfo = baseInfoStore.getContractCoinStaticItemDataBySymbolId(klineStore.currentSymbolId!)
      inputLeverage.value++
      if (currentSymbolInfo && inputLeverage.value > currentSymbolInfo.maxLeverage) {
        inputLeverage.value = currentSymbolInfo.maxLeverage
      }
    }

    const leverageList = computed(() => {
      const currentSymbolInfo = baseInfoStore.getContractCoinStaticItemDataBySymbolId(klineStore.currentSymbolId!)
      if (!currentSymbolInfo) return []
      // 按照maxLeverage， 取一个从1开始，到按照maxLeverage结束，且中间的值是5的倍数的数组，数组长度为6
      const list = []
      const step = currentSymbolInfo.maxLeverage / 5
      for (let i = 0; i < 6; i++) {
        let value = step * i
        if (value < 1) {
          value = 1
        }
        if (value > currentSymbolInfo.maxLeverage) {
          value = currentSymbolInfo.maxLeverage
        }
        list.push(value)
      }
      return list
    })

    const sliderMarks = computed(() => {
      const marks: Record<number, string> = {}
      for (const item of leverageList.value) {
        marks[item] = `${item}X`
      }
      return marks
    })

    const openMaxAmount = computed(() => {
      const maintMarginRate = exchangeInfo.getMaintMarginRateBySymbolId(klineStore.currentSymbolId!)
      if (!maintMarginRate) return '--'
      const maintAmount = maintMarginRate.find((item) => item.maxLeverage >= inputLeverage.value)
      return maintAmount?.maxAmount
    })

    const handleSliderInput = (value: Arrayable<number>) => {
      inputLeverage.value = value as number
    }

    const handleConfirm = async () => {
      const isSuccess = await orderer.updateLeverage(inputLeverage.value)
      if (isSuccess) {
        props.onSuccess?.()
      }
    }

    onBeforeUnmount(() => {
      inputLeverage.value = orderer.currentLeverage
    })

    return () => (
      <div class="update-leverage-layout update-leverage">
        <div class="input-leverage-container" onClick={() => inputLeverageRef.value?.focus()}>
          <div class="control-btn" onClick={handleMinus}>
            <MinusIcon />
          </div>

          <div class="input-leverage-container-input">
            <InputView modelValue={inputLeverage.value} isNumber ref={inputLeverageRef} />
            <div class="suffix-container">X</div>
          </div>

          <div class="control-btn" onClick={handlePlus}>
            <PlusIcon2 />
          </div>
        </div>

        <SizedBox height={8} />
        <HocElSlider v-model={inputLeverage.value} min={1} marks={sliderMarks.value} max={leverageList.value[leverageList.value.length - 1]} onInput={handleSliderInput} />

        <div class="max-tip">{t('当前杠杆倍数最高可开: {max} USDT', { max: openMaxAmount.value?.formatNumberWithCommas() || '--' })}</div>
        <div class="max-tip">{t('选择超过[10x]杠杆交易会增加强行平仓风险，请注意相关风险，更多信息请参考交易指南')}</div>

        <CustomButton class={'confirm-button'} type={'primary'} loading={orderer.updateLeverageLoading} onClick={handleConfirm}>
          {t('确认')}
        </CustomButton>
      </div>
    )
  },
})
